<template>
  <v-card 
    v-if="isVisible"
    class="custom-reports-panel mb-6" 
    :style="sectionStyle"
    elevation="0">
    <v-card-title class="pb-2 d-flex justify-space-between align-center">
      <h3 class="panel-title text-h6 font-weight-semibold mb-0">Custom Reports</h3>
      <v-btn icon @click="handleOpenInNewTab" large>
        <v-icon color="secondary">mdi-open-in-new</v-icon>
      </v-btn>
    </v-card-title>

    <!-- Search Bar -->
    <v-card-text v-if="!loading" class="pa-4 pb-2">
      <v-text-field
        v-model="searchQuery"
        placeholder="Search reports..."
        prepend-inner-icon="mdi-magnify"
        dense
        outlined
        hide-details
        clearable
        style="max-width: 100%;"
        @input="handleSearchChange"
      ></v-text-field>
    </v-card-text>

    <v-card-text v-if="loading" class="text-center py-8">
      <v-progress-circular indeterminate color="#ff1463"></v-progress-circular>
    </v-card-text>

    <v-card-text v-else-if="filteredReports.length === 0" class="empty-state-text text-center py-4">
      {{ searchQuery ? 'No reports found matching your search' : 'No custom reports' }}
    </v-card-text>

    <v-list v-else dense>
      <v-list-item 
        v-for="report in paginatedItems" 
        :key="report.id" 
        :to="`/report/${report.id}`"
        target="_blank"
        class="report-item hover-grey-lighten-5">
        <v-list-item-icon>
          <v-icon class="report-icon">mdi-chart-bubble</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title class="report-title text-body-2 font-weight-medium">
            {{ stripHTML(report.title) }}
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </v-list>

    <!-- Pagination -->
    <section-pagination-footer
      :show="!loading && showPagination"
      :text="paginationText"
      :can-prev="canGoToPreviousPage"
      :can-next="canGoToNextPage"
      @prev="goToPreviousPage"
      @next="goToNextPage"
    />
  </v-card>
</template>

<script>
import PaginationMixin from '../mixins/PaginationMixin.js';
import SectionPaginationFooter from '../common/SectionPaginationFooter.vue';

export default {
  name: 'CustomReportsPanel',

  mixins: [PaginationMixin],

  components: {
    SectionPaginationFooter
  },
  
  props: {
    reports: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    isVisible: {
      type: Boolean,
      default: true
    },
    sectionStyle: {
      type: String,
      default: ''
    },
    itemsPerPage: {
      type: Number,
      default: 10
    }
  },

  data() {
    return {
      searchQuery: ''
    };
  },

  computed: {
    filteredReports() {
      if (!this.searchQuery) {
        return this.reports;
      }
      const query = this.searchQuery.toLowerCase();
      return this.reports.filter(report => {
        const title = this.stripHTML(report.title || '').toLowerCase();
        return title.includes(query);
      });
    },

    /**
     * Items to paginate.
     * Components should override this with a computed that returns the array
     * they want to paginate (e.g. matches, displayedContracts, normalizedMatches).
     */
    paginationItems() {
      return this.filteredReports || [];
    }
  },

  watch: {
    searchQuery() {
      // Reset to first page when search query changes
      this.$emit('page-change', 1);
    }
  },

  methods: {
    stripHTML(text) {
      if (!text) return "";
      return text.replace(/(<([^>]+)>)/gi, "");
    },

    handleOpenInNewTab() {
      this.$emit('open-in-new-tab', { name: 'storyboards' });
    },

    handleSearchChange() {
      // Emit search change event if needed
      this.$emit('search-change', this.searchQuery);
    }
  }
};
</script>

<style scoped>
/* Card Container */
.custom-reports-panel {
  border: 1px solid #e5e7eb;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
}

/* Title */
.panel-title {
  color: #1f2937;
}

/* Empty State */
.empty-state-text {
  color: #6b7280;
}

/* Report Items */
.report-item {
  cursor: pointer;
}

.report-icon {
  color: #6b7280;
}

.report-title {
  color: #1f2937;
}

.hover-grey-lighten-5:hover {
  background-color: #fafafa !important;
}

/* Pagination */
.pagination-container {
  border-top: 1px solid #e5e7eb;
}

.pagination-wrapper {
  width: 100%;
}

.pagination-text {
  color: #6b7280;
}

.pagination-button {
  color: #6b7280;
}

.gap-2 {
  gap: 8px;
}
</style>
